import { useCallback, useRef, useEffect } from 'react';
import { useMotionValue, animate } from 'framer-motion';

const THRESHOLD_PX = 80;

// Static — never changes, so defined once outside the hook
const BACKDROP_ELEMENT = (
  <div
    style={{
      position: 'fixed',
      inset: 0,
      backgroundColor: '#F2F2F7',
      pointerEvents: 'none',
      zIndex: -1,
    }}
  />
);

/**
 * useSwipeBack — swipe-right gesture for Detail pages.
 *
 * Drag right past 80px to navigate back. Callers pass their onBack
 * function directly — the hook handles ref stability internally.
 *
 * @param {Function} onBack — called AFTER off-screen animation completes
 * @param {boolean} enabled — false disables the gesture (e.g., overlay open)
 * @returns {{ dragProps, swipeStyle, backdropElement }}
 */
const useSwipeBack = (onBack, enabled = true) => {
  const x = useMotionValue(0);
  const navigating = useRef(false);
  const onBackRef = useRef(onBack);

  // Keep ref current without recreating the drag handler
  useEffect(() => {
    onBackRef.current = onBack;
  });

  const handleDragEnd = useCallback(
    (_, info) => {
      if (navigating.current) return;

      const screenWidth = window.innerWidth;
      const threshold = Math.min(THRESHOLD_PX, screenWidth * 0.25);

      if (info.offset.x > threshold) {
        navigating.current = true;
        animate(x, screenWidth, { duration: 0.2 }).then(() => {
          try {
            onBackRef.current?.();
          } finally {
            navigating.current = false;
            x.set(0);
          }
        });
      } else {
        animate(x, 0, { type: 'spring', stiffness: 500, damping: 30 });
      }
    },
    [x]
  );

  // dragProps: spread onto motion.div (no style — use swipeStyle separately)
  const dragProps = enabled
    ? {
        drag: 'x',
        dragConstraints: { left: 0, right: 0 },
        dragElastic: { left: 0, right: 0.5 },
        dragMomentum: false,
        onDragEnd: handleDragEnd,
      }
    : {};

  // swipeStyle: merge into the consumer's style prop
  const swipeStyle = enabled ? { x } : {};

  return { dragProps, swipeStyle, backdropElement: BACKDROP_ELEMENT };
};

export default useSwipeBack;
